<template>
  <div class="mt-2">
    <v-row>
      <v-col cols="6">
        <v-menu
          ref="datesPickerMenu"
          v-model="showDatesPicker"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="dateRangeText"
              :label="$t('booking.dates')"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              color="grey"
            />
          </template>
          <v-date-picker
            v-model="dates"
            no-title
            scrollable
            :locale="this.$i18n.locale"
            color="red darken-1"
            range
            :events="onDateEvents"
            :allowedDates="allowedDates"
          />
        </v-menu>
      </v-col>

      <v-col cols="3">
        <v-menu
          ref="timeStartPickerMenu"
          v-model="showTimeStartPicker"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="timeStart"
              :label="$t('booking.startTime')"
              prepend-icon="mdi-clock-time-four-outline"
              readonly
              v-bind="attrs"
              v-on="on"
              color="grey"
            />
          </template>
          <v-time-picker
            v-if="showTimeStartPicker"
            v-model="timeStart"
            full-width
            @click:minute="$refs.timeStartPickerMenu.save(timeStart)"
            format="24hr"
            color="red lighten-1"
            :allowed-hours="allowedHours"
            :allowed-minutes="allowedMinutes"
            no-title
            min="9:00"
            :max="maxTime"
          />
        </v-menu>
      </v-col>
      <v-col cols="3">
        <v-menu
          ref="timeEndPickerMenu"
          v-model="showTimeEndPicker"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="timeEnd"
              :label="$t('booking.endTime')"
              prepend-icon="mdi-clock-time-four-outline"
              readonly
              v-bind="attrs"
              v-on="on"
              color="grey"
            />
          </template>
          <v-time-picker
            v-if="showTimeEndPicker"
            v-model="timeEnd"
            full-width
            @click:minute="$refs.timeEndPickerMenu.save(timeEnd)"
            format="24hr"
            color="red lighten-1"
            :allowed-hours="allowedHours"
            :allowed-minutes="allowedMinutes"
            no-title
            :min="minTime"
            max="18:00"
          />
        </v-menu>
      </v-col>
    </v-row>

    <p class="text-subtitle-1 dateRangeReadable" v-html="dateRangeReadable"></p>
  </div>
</template>

<script>
import { DateTime, Interval } from "luxon";
import i18n from "@/i18n";

export default {
  name: "DateRangePicker",
  components: {},
  props: {
    dateEvents: null,
  },
  data: () => ({
    showDatesPicker: false,
    showTimeStartPicker: false,
    showTimeEndPicker: false,
    dates: [
      new Date(Date.now()).toISOString().substr(0, 10),
      new Date(Date.now()).toISOString().substr(0, 10),
    ],
    timeStart: "9:00",
    timeEnd: "18:00",
  }),
  computed: {
    dateStart() {
      return this.dates[0];
    },
    dateEnd() {
      return this.dates.length === 1 ? this.dates[0] : this.dates[1];
    },
    isSingleDate() {
      return (
        this.dates.length === 1 ||
        (this.dates.length === 2 && this.dates[0] === this.dates[1])
      );
    },
    isDateRange() {
      return this.dates.length === 2 && this.dates[0] !== this.dates[1];
    },
    dateRangeText() {
      if (this.isSingleDate) return this.formatDateMed(this.dateStart);
      else if (this.isDateRange)
        return i18n
          .t("booking.dateRange")
          .replace("%1", this.formatDateMed(this.dateStart))
          .replace("%2", this.formatDateMed(this.dateEnd));
      return this.dates.join(" ~ ");
    },
    dateRangeReadable() {
      if (this.isSingleDate) {
        if (this.timeStart && this.timeEnd)
          return i18n
            .t("booking.readableSingleDateFromTo")
            .replace("%1", this.boldify(this.formatDateFull(this.dateStart)))
            .replace("%2", this.boldify(this.timeStart))
            .replace("%3", this.boldify(this.timeEnd));
        else if (this.timeStart && !this.timeEnd)
          return i18n
            .t("booking.readableSingleDateFrom")
            .replace("%1", this.boldify(this.formatDateFull(this.dateStart)))
            .replace("%2", this.boldify(this.timeStart));
        else if (!this.timeStart && this.timeEnd)
          return i18n
            .t("booking.readableSingleDateTo")
            .replace("%1", this.boldify(this.formatDateFull(this.dateStart)))
            .replace("%2", this.boldify(this.timeEnd));
        else
          return i18n
            .t("booking.readableSingleDate")
            .replace("%1", this.boldify(this.formatDateFull(this.dateStart)));
      } else if (this.isDateRange)
        return i18n
          .t("booking.readableDateRange")
          .replace("%1", this.boldify(this.formatDateFull(this.dateStart)))
          .replace("%3", this.boldify(this.formatDateFull(this.dateEnd)))
          .replace(
            "%2",
            this.timeStart
              ? i18n
                  .t("booking.readableDateRangeTime")
                  .replace("%1", this.boldify(this.timeStart))
              : ""
          )
          .replace(
            "%4",
            this.timeEnd
              ? i18n
                  .t("booking.readableDateRangeTime")
                  .replace("%1", this.boldify(this.timeEnd))
              : ""
          );
      return this.dates.join(" ~ ");
    },
    minTime() {
      if (this.isSingleDate) return this.timeStart;
      else return null;
    },
    maxTime() {
      if (this.isSingleDate) return this.timeEnd;
      else return null;
    },
  },
  methods: {
    allowedDates: (d) =>
      DateTime.fromFormat(d, "yyyy-MM-dd").startOf("day") >=
      DateTime.now().startOf("day"),
    allowedHours: (h) => h >= 9 && h <= 18,
    allowedMinutes: (m) => m % 30 === 0,

    boldify(text) {
      return `<bold>${text}</bold>`;
    },
    formatDateMed(date) {
      return DateTime.fromISO(date)
        .setLocale(this.$i18n.locale)
        .toLocaleString(DateTime.DATE_MED);
    },
    formatDateFull(date) {
      return DateTime.fromISO(date)
        .setLocale(this.$i18n.locale)
        .toLocaleString(DateTime.DATE_FULL);
    },

    ensureDatesOrder(dates) {
      if (dates.length === 2) {
        const dateFrom = DateTime.fromISO(dates[0]);
        const dateTo = DateTime.fromISO(dates[1]);
        if (dateTo < dateFrom) {
          const temp = this.dates[0];
          this.dates[0] = this.dates[1];
          this.dates[1] = temp;
        }
      }
    },
    ensureDatesRange() {
      if (this.dates.length === 1) this.dates.push(this.dates[0]);
    },

    makeDateTime(date, time) {
      return DateTime.fromFormat(`${date}&${time}`, "yyyy-MM-dd&H:mm");
    },
    getStartDateTime() {
      return this.makeDateTime(this.dates[0], this.timeStart);
    },
    getEndDateTime() {
      if (this.isSingleDate)
        return this.makeDateTime(this.dates[0], this.timeEnd);
      else return this.makeDateTime(this.dates[1], this.timeEnd);
    },
    reset() {
      this.showDatesPicker = false;
      this.showTimeStartPicker = false;
      this.showTimeEndPicker = false;
      this.dates = [
        new Date(Date.now()).toISOString().substr(0, 10),
        new Date(Date.now()).toISOString().substr(0, 10),
      ];
      this.timeStart = "9:00";
      this.timeEnd = "18:00";
    },
    onDateTimeInput() {
      this.$emit(
        "change",
        Interval.fromDateTimes(this.getStartDateTime(), this.getEndDateTime())
      );
    },
    onDateEvents(date) {
      return this.dateEvents
        ? this.dateEvents(DateTime.fromFormat(date, "yyyy-MM-dd"))
        : null;
    },
  },
  watch: {
    dates(newValue) {
      this.ensureDatesOrder(newValue);
    },
    showDatesPicker(open) {
      if (!open) {
        this.ensureDatesRange();
        this.onDateTimeInput();
      }
    },
    showTimeStartPicker(open) {
      if (!open) this.onDateTimeInput();
    },
    showTimeEndPicker(open) {
      if (!open) this.onDateTimeInput();
    },
  },
  mounted() {
    this.onDateTimeInput();
  },
};
</script>

<style lang="scss" scoped>
.dateRangeReadable::v-deep bold {
  font-weight: bold;
}
</style>
